import { format } from "date-fns";
import Highcharts, { GradientColorObject, Options } from "highcharts";
import { StandaloneTankTATEnum } from "../../common/enums/StandaloneTankTATEnum";
import { IEntityValueDeviceModel, IEntityValueTankModel } from "../../common/models/IEntityValueModels";
import { DATE_FORMAT_STRING, TIME_FORMAT_STRING } from "../../common/utilities/TimeframeUtilities";
import i18n from "../../services/localization-service/i18n";
import { Theme } from "@mui/material";
import { themifyHighchartsOptions } from "../../common/utilities/ThemifyHighchartsOptions";

export interface ITankTagDisplayValue {
    name: string,
    value: string,
    date: string;
    time?: string,
    tatID?: number
}

const FEET_UOM_ID = 19;

export function getTankHeights(tank: IEntityValueDeviceModel) {
    const liquidHeightTag = tank.tags.find(o => o.tatID === StandaloneTankTATEnum.LiquidHeight);

    const liquidHeightFeet = liquidHeightTag?.uomID === FEET_UOM_ID
        ? liquidHeightTag?.tagValue?.value ?? 0
        : (liquidHeightTag?.tagValue?.value ?? 0) / 12;

    const tankBottomHeightFeet = (tank.tankStyle.elevationInInches ?? 0) / 12;
    const tankTopHeightFeet = tankBottomHeightFeet + (tank.tankStyle.heightInInches / 12);
    const overflow: boolean = liquidHeightFeet > tankTopHeightFeet;

    return { liquidHeightFeet, tankBottomHeightFeet, tankTopHeightFeet, overflow };
}

const perShapeGradient = {
    x1: 0,
    y1: 0,
    x2: 1,
    y2: 0
};

export const chartColors: GradientColorObject[] = [{
    linearGradient: perShapeGradient,
    stops: [
        [0, 'rgb(128,0,0)'],
        [0.1, 'rgb(204,0,0)'],
        [0.25, 'rgb(245,128,128)'],
        [0.5, 'rgb(230,75,75)'],
        [0.75, 'rgb(220,50,50)'],
        [0.9, 'rgb(230,75,75)'],
        [1, 'rgb(128,0,0)']
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgb(46, 150, 208)'],
        [0.5, 'rgb(120, 202, 248)'],
        [1, 'rgb(46, 150, 208)']
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgb(112, 180, 5)'],
        [0.5, 'rgb(136, 219, 5)'],
        [1, 'rgb(112, 180, 5)'],
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgba(192, 192, 192, .5)'],
        [0.5, 'rgba(245, 245, 245, .5)'],
        [1, 'rgba(192, 192, 192, .5)'],
    ]
}];

export function getChartOptions(
    tank: IEntityValueDeviceModel,
    theme: Theme,
): Options {

    const { liquidHeightFeet, tankBottomHeightFeet, tankTopHeightFeet, overflow } = getTankHeights(tank);
    const series: Highcharts.SeriesColumnOptions[] = [];
    const remainingHeightFeet: number = tankTopHeightFeet - liquidHeightFeet;

    if (!overflow) {
        series.push({
            color: chartColors[3],
            type: "column",
            data: [remainingHeightFeet],
            name: i18n.t('liquidProd.tankRemainingCapacity') as string,
            yAxis: 0,
            showInLegend: false
        });
    }

    series.push({
        color: tank.subTypeName?.toLowerCase() === 'water' ? chartColors[1] : chartColors[2],
        type: "column",
        data: [liquidHeightFeet],
        name: tank.subTypeName,
        yAxis: 0,
        showInLegend: false,
    });

    const feetMin: number = tankBottomHeightFeet;
    const feetMax: number = tankTopHeightFeet;

    const options: Options = {
        chart: {
            ignoreHiddenSeries: false,
            type: "column",
            margin: 0,
            spacing: [0, 0, 0, 0],
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
        title: {
            text: undefined
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            column: {
                pointPadding: 0,
                groupPadding: 0,
                stacking: 'normal'
            }
        },
        series: series,
        xAxis: [{
            visible: false
        }],
        yAxis: [{
            visible: false,
            min: feetMin,
            max: feetMax
        }]
    };

    return themifyHighchartsOptions(options, theme);
}

export function getTagDisplayText(tank: IEntityValueTankModel, tat: number) {

    let label: string = '';

    switch (tat) {

        case StandaloneTankTATEnum.LiquidHeight:
            label = i18n.t('objectTagValues.liquidHeight');
            break;

        default:
            break;
    }

    const tag = tank.tags.find(o => o.tatID === tat);
    const value = tag?.tagValue?.displayText;

    let date = '...';
    let time = '';

    if (value) {
        date = format(new Date(tag?.tagValue?.timestamp as string), DATE_FORMAT_STRING);
        time = format(new Date(tag?.tagValue?.timestamp as string), TIME_FORMAT_STRING);
    }

    const item: ITankTagDisplayValue = {
        name: label,
        value: value ? `${value} ${tag?.tagValue?.unitDisplayName}` : '...',
        date: date,
        time: time,
        tatID: tat
    };

    return item;
}
